import { ExclamationTriangleIcon, InformationCircleIcon } from '@heroicons/react/24/outline'
import type { InterviewScreenShareIssue } from '../../utils/interviewScreenShare'

export type InterviewScreenShareModalVariant = 'required' | 'wrong_choice'

interface InterviewScreenShareModalProps {
  variant: InterviewScreenShareModalVariant
  issue: InterviewScreenShareIssue | null
  pickerCancelled?: boolean
  /** True while the mic/share request is in flight — shows a spinner and
   * disables the buttons so the click doesn't feel unresponsive during the
   * few seconds of permission prompts and the audio-detection wait. */
  isSubmitting?: boolean
  onOpenShare: () => void
  onCancel: () => void
}

const ButtonSpinner = () => (
  <span className="inline-block h-4 w-4 animate-spin rounded-full border-2 border-white border-b-transparent" />
)

export default function InterviewScreenShareModal({
  variant,
  issue,
  pickerCancelled,
  isSubmitting,
  onOpenShare,
  onCancel,
}: InterviewScreenShareModalProps) {
  if (variant === 'wrong_choice') {
    return (
      <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/60 p-4 backdrop-blur-[2px]">
        <div
          className="w-full max-w-md rounded-2xl bg-white p-8 text-center shadow-2xl ring-1 ring-gray-200/80"
          role="dialog"
          aria-labelledby="share-wrong-title"
        >
          <div className="mx-auto mb-5 flex h-16 w-16 items-center justify-center rounded-full bg-sky-100">
            <InformationCircleIcon className="h-10 w-10 text-sky-600" aria-hidden />
          </div>
          <h2 id="share-wrong-title" className="text-xl font-semibold tracking-tight text-gray-900">
            Share Entire Screen with System Audio
          </h2>
          <p className="mt-3 text-sm leading-relaxed text-gray-600">
            Choose <strong className="text-gray-800">Entire screen</strong> in the browser dialog (not{' '}
            <strong className="text-gray-800">Chrome Tab</strong> or <strong className="text-gray-800">Window</strong>
            ). Turn on <strong className="text-gray-800">Also share system audio</strong>, then click{' '}
            <strong className="text-gray-800">Share</strong>.
          </p>
          {issue === 'no_system_audio' && (
            <p className="mt-3 rounded-lg bg-amber-50 px-3 py-2 text-sm text-amber-900 ring-1 ring-amber-200/80">
              System audio was off or missing. Enable &quot;Also share system audio&quot; before sharing.
            </p>
          )}
          {issue === 'silent_system_audio' && (
            <p className="mt-3 rounded-lg bg-amber-50 px-3 py-2 text-sm text-amber-900 ring-1 ring-amber-200/80">
              We can&apos;t hear the candidate yet. Ask them to say something out loud — this will resolve
              automatically once we detect their voice, no need to share again.
              <br />
              <br />
              Still nothing after they speak? Check that your call&apos;s audio is playing through this
              computer&apos;s default speakers or headset, not a different device.
            </p>
          )}
          {(issue === 'not_entire_screen' || issue === 'unknown_surface') && (
            <p className="mt-3 rounded-lg bg-amber-50 px-3 py-2 text-sm text-amber-900 ring-1 ring-amber-200/80">
              A tab or window was shared. You must select your full display under the Entire screen tab.
            </p>
          )}
          <ol className="mt-6 space-y-2.5 text-left text-sm text-gray-700">
            <li className="flex gap-3">
              <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-gray-100 text-xs font-semibold text-gray-700">
                1
              </span>
              <span>
                Open the <strong className="text-gray-900">Entire screen</strong> tab in the picker.
              </span>
            </li>
            <li className="flex gap-3">
              <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-gray-100 text-xs font-semibold text-gray-700">
                2
              </span>
              <span>Click your monitor thumbnail so the whole desktop is selected.</span>
            </li>
            <li className="flex gap-3">
              <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-gray-100 text-xs font-semibold text-gray-700">
                3
              </span>
              <span>
                Switch <strong className="text-gray-900">Also share system audio</strong> to{' '}
                <strong className="text-gray-900">On</strong>.
              </span>
            </li>
            <li className="flex gap-3">
              <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-gray-100 text-xs font-semibold text-gray-700">
                4
              </span>
              <span>
                Click <strong className="text-gray-900">Share</strong>. Allow the microphone if prompted.
              </span>
            </li>
          </ol>
          <button
            type="button"
            onClick={onOpenShare}
            disabled={isSubmitting}
            className="mt-8 flex w-full items-center justify-center gap-2 rounded-xl bg-blue-600 py-3 text-sm font-semibold text-white shadow-md transition hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-70"
          >
            {isSubmitting && <ButtonSpinner />}
            {isSubmitting ? 'Opening…' : 'Try Again'}
          </button>
        </div>
      </div>
    )
  }

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/60 p-4 backdrop-blur-[2px]">
      <div
        className="w-full max-w-md rounded-2xl bg-white p-8 text-center shadow-2xl ring-1 ring-gray-200/80"
        role="dialog"
        aria-labelledby="share-required-title"
      >
        <div className="mx-auto mb-5 flex h-16 w-16 items-center justify-center rounded-full bg-amber-50 ring-2 ring-amber-200/90">
          <ExclamationTriangleIcon className="h-10 w-10 text-amber-600" aria-hidden />
        </div>
        <h2 id="share-required-title" className="text-xl font-semibold tracking-tight text-gray-900">
          Screen Share Required
        </h2>
        <p className="mt-3 text-sm leading-relaxed text-gray-600">
          This recording requires your <strong className="text-gray-800">entire screen</strong> and{' '}
          <strong className="text-gray-800">system audio</strong>. Your browser will open its own dialog—you must pick
          Entire screen, enable system audio, then Share. Microphone access is also needed for your voice.
        </p>
        {pickerCancelled && (
          <p className="mt-4 rounded-lg bg-gray-50 px-3 py-2 text-sm text-gray-700 ring-1 ring-gray-200">
            You closed the share dialog. Screen capture is required before the interview can start.
          </p>
        )}
        <ul className="mt-5 space-y-2 text-left text-sm text-gray-600">
          {/* <li className="flex gap-2">
            <span className="text-blue-600">•</span>
            Use Chrome, Edge, or Firefox on a secure connection (HTTPS or localhost).
          </li> */}
          <li className="flex gap-2">
            <span className="text-blue-600">•</span>
            Do not share only a tab or a single app window unless your browser labels it as full screen capture.
          </li>
        </ul>
        <div className="mt-8 flex flex-col gap-3 sm:flex-row sm:justify-center">
          <button
            type="button"
            onClick={onOpenShare}
            disabled={isSubmitting}
            className="flex flex-1 items-center justify-center gap-2 rounded-xl bg-blue-600 py-3 text-sm font-semibold text-white shadow-md ring-2 ring-blue-400/40 transition hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-70"
          >
            {isSubmitting && <ButtonSpinner />}
            {isSubmitting ? 'Opening…' : 'Open Share Dialog'}
          </button>
          <button
            type="button"
            onClick={onCancel}
            disabled={isSubmitting}
            className="flex-1 rounded-xl bg-gray-600 py-3 text-sm font-semibold text-white transition hover:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-70"
          >
            Cancel
          </button>
        </div>
      </div>
    </div>
  )
}
